/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {
  CellClassParams,
  CellValueChangedEvent,
  Column,
  EditableCallbackParams,
  FillOperationParams,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  ModelUpdatedEvent,
  ProcessCellForExportParams,
  RowGroupOpenedEvent,
  RowSelectedEvent,
} from '@ag-grid-community/core';
import { NewValueParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { SelectionChangedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import { cloneDeep, differenceWith, find, get, isEqual, omit } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Subject, Subscription } from 'rxjs';
import { filter, finalize, startWith, switchMap } from 'rxjs/operators';

import { ToggleBudgetCurrencyComponent } from '@widgets/budget-toggle-currency/toggle-budget-currency.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { ExportFullDatasetButtonComponent } from '@features/export-full-dataset-button/export-full-dataset-button.component';
import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import {
  ReasonModalData,
  ReasonModalModalComponent,
  ReasonModalResponse,
} from '@shared/components/modals/reason-modal/reason-modal.component';
import { PaginationGridComponent } from '@shared/components/pagination-grid/pagination-grid.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { MessagesConstants } from '@shared/constants/messages.constants';
import {
  DEFAULT_PAGINATION_SIZE,
  initialPaginationSize,
} from '@shared/constants/pagination.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ApiService } from '@shared/services/api.service';
import { DatasourceService } from '@shared/services/datasource.service';
import { DocumentLibraryFile } from '@shared/services/document-library.service';
import {
  AmountType,
  Currency,
  DocumentType,
  EntityType,
  EventType,
  GqlService,
  InvoiceDataStream,
  InvoiceStatus,
  listDocumentsQuery,
  listVendorAmountTypesQuery,
  PermissionType,
  TrialImplementationStatus,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { BudgetCurrencyType } from '@shared/store/budget-currency/budget-currency.model';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceService, isInvoiceTotalEqual } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import {
  AgSetColumnsVisible,
  decimalEquality,
  ServerSideFilterInfo,
  ServerSideSortOrder,
} from '@shared/utils';
import { DataStreamUtils } from '@shared/utils/datastream.utils';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { ArrElement, ExportType, ValuesType } from '@shared/utils/utils';

import {
  BulkEditInvoicesComponent,
  BulkEditInvoicesResponse,
} from './bulk-edit-invoices/bulk-edit-invoices.component';
import { DetailCellRendererComponent } from './detail-cell-renderer/detail-cell-renderer.component';
import { InvoiceFiltersComponent } from './invoice-filters/invoice-filters.component';
import { InvoicesSyncButtonsComponent } from './invoices-buttons/invoices-sync-buttons.component';
import { InvoicesColumnDefs } from './invoices-grid-config';
import { InvoicesGridFormatterService } from './invoices-grid-formatter.service';
import { InvoicesPageService } from './invoices-page.service';
import { InvoiceCheckboxTooltip, ServerSideFilters } from './invoices.constants';
import { ErrorRow, IntegrationLineItem } from './invoices.model';
import { NewInvoiceDialogComponent } from './new-invoice-dialog/new-invoice-dialog.component';
import { PeriodCloseComponent } from '../../../period-close.component';

const getQueryParamData = () => {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const invoiceService = inject(InvoiceService);

  route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
    if (params['accrualPeriod']) {
      const accrualPeriod = dayjs(params['accrualPeriod']).format('YYYY-MM-DD');

      invoiceService.setAccrualPeriodsAndVendorFilter([accrualPeriod]);
      void router.navigate([], { queryParams: {}, replaceUrl: true });
    }
  });
};

export const invoiceDisabledTooltip = 'Cannot edit invoice in closed month.';

export interface IInvoicesGridData extends InvoiceModel {
  attributes: { attr_name: string; attr_total: number }[];
  file?: DocumentLibraryFile;
  has_prepaid: boolean;
  hasPrepaidInitialValue: boolean;
  initialRowIndex: number;
}

@Component({
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [
    CheckboxComponent,
    InvoicesSyncButtonsComponent,
    ButtonComponent,
    TooltipDirective,
    InvoiceFiltersComponent,
    ToggleBudgetCurrencyComponent,
    PaginationGridComponent,
    ExportExcelButtonComponent,
    ExportFullDatasetButtonComponent,
    TablePanelComponent,
    TableSkeletonComponent,
  ],
  selector: 'aux-invoices',
  styleUrl: 'invoices.component.scss',
  templateUrl: './invoices.component.html',
})
export class InvoicesComponent implements AfterViewInit, OnDestroy {
  apiService = inject(ApiService);
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  datasourceService = inject(DatasourceService);
  destroyRef = inject(DestroyRef);
  eventQuery = inject(EventQuery);
  eventService = inject(EventService);
  globalStore = inject(GlobalStore);
  gqlService = inject(GqlService);
  injector = inject(Injector);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  invoiceService = inject(InvoiceService);
  invoicesGridFormatterService = inject(InvoicesGridFormatterService);
  invoicesPageService = inject(InvoicesPageService);
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);
  organizationQuery = inject(OrganizationQuery);
  organizationService = inject(OrganizationService);
  overlayService = inject(OverlayService);
  periodCloseComponent = inject(PeriodCloseComponent);
  route = inject(ActivatedRoute);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);

  permissions = injectPermissions({
    hasDeleteInvoice: 'PERMISSION_DELETE_INVOICE',
    hasEditInvoice: 'PERMISSION_UPDATE_INVOICES',
  });

  editMode = signal(false);
  errorRows = signal<Record<string, ErrorRow>>({});
  gridAPI = signal<GridApi | null>(null);
  hasChanges = signal(false);
  listVendorAmountTypesLoading = signal(true);
  paginationPageSize = signal(DEFAULT_PAGINATION_SIZE);
  rowsToDelete = signal<{ invoice: InvoiceModel; reason: string }[]>([]);
  savingChanges = signal(false);
  selectedBudgetCurrencyType = signal<BudgetCurrencyType>(BudgetCurrencyType.VENDOR);
  selectedRows = signal<InvoiceModel[]>([]);
  showErrors = signal(false);
  showExportFull = signal(false);
  totalRows = signal(0);
  waitingUpdateQuarterAccrualsEvent = signal(false);

  deleteButtonTooltip = computed(() => {
    const permissionTooltip = this.permissions().hasDeleteInvoice
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;

    return this.invoiceCtx.invoiceLockTooltip() || permissionTooltip;
  });
  editButtonTooltip = computed(() => {
    const permissionTooltip = this.permissions().hasEditInvoice
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;

    return this.invoiceCtx.invoiceLockTooltip() || permissionTooltip;
  });
  isJEConfigSetToAttribute = computed(() => {
    return !!this.globalStore.primaryAttribute.attribute_name();
  });
  isVendorCurrency = computed(() => {
    return this.selectedBudgetCurrencyType() === BudgetCurrencyType.VENDOR;
  });

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(() => {
    return (
      this.trialAttributesLoading() ||
      this.listVendorAmountTypesLoading() ||
      this.waitingUpdateQuarterAccrualsEvent()
    );
  });

  readonly datasource = this.datasourceService.invoiceLibraryDatasource;
  detailCellRenderer = DetailCellRendererComponent;
  files = new Map<string, null | ValuesType<listDocumentsQuery['items']>>();
  filterValues$ = new BehaviorSubject<Record<string, unknown>>({});
  gridOptions = this.getGridOptions();
  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  initialValues: IInvoicesGridData[] = [];
  invoicesDepositsEnabled = this.launchDarklyService.$select((flags) => flags.invoices_deposits);
  isAllRowsOnCurrentPageSelected = false;
  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES;
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(this.workflowName);
  isVendorCurrenciesUSD = true;
  newPrepaidImpactRows: {
    id: string;
    index: number;
    vendorId: string;
  }[] = [];
  readonly paginationKey = LocalStorageKey.PAGINATION_INVOICES;
  prepaidEnabled = this.launchDarklyService.$select((flags) => flags.prepaids);
  refresh$ = new Subject<void>();
  savingChanges$ = toObservable(this.savingChanges);
  serverSideFilters: ServerSideFilterInfo<InvoiceDataStream>[] = ServerSideFilters;
  showInvalidCostCategorization = this.invoiceService.showInvalidCostCategorization;
  showInvoiceDetailLink = this.launchDarklyService.$select((flags) => flags.invoice_detail);
  showInvoicesPaymentDate = this.launchDarklyService.$select(
    (flags) => flags.invoices_payment_date
  );
  showInvoicesPaymentStatus = this.launchDarklyService.$select(
    (flags) => flags.invoices_payment_status
  );
  showRequireAccrualPeriod = this.invoiceService.showRequireAccrualPeriod;
  showRequireCostBreakdown = this.invoiceService.showRequireCostBreakdown;
  showUnpaidInvoiceCheckbox = this.launchDarklyService.$select(
    (flags) => flags.invoices_payment_date
  );
  showUnpaidInvoices = this.invoiceService.showUnpaidInvoices;
  sortChanged$ = new Subject();
  sortModel$ = new BehaviorSubject<Array<ServerSideSortOrder<InvoiceDataStream>>>([]);
  userHasApproveInvoicePermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_APPROVE_INVOICE],
    sysAdminsOnly: false,
  });
  userHasUpdatePrepaidPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_PREPAIDS],
    sysAdminsOnly: false,
  });
  vendorCostCategories: listVendorAmountTypesQuery[] = [];
  vendorCurrencyEnabled = this.launchDarklyService.$select((flags) => flags.vendor_currency);
  vendorHasOneCurrency = true;
  vendorHasOneCurrencySubscription: Subscription | undefined;

  ngAfterViewInit() {
    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => this.onNewInvoice(), this.invoiceCtx.disabledAddInvoiceUI);
    });

    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(
        ({ modalVendorId }) => this.onNewInvoice(modalVendorId),
        this.invoiceCtx.disabledAddInvoiceUI,
        'modalVendorId'
      );
    });
  }

  ngOnDestroy(): void {
    this.invoiceService.filtersForm.enable();
  }

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellValueChanged(event: CellValueChangedEvent) {
    if (event.column.getColId() === 'vendor_id') {
      const data = { ...event.data, po_reference: null };

      event.node.setData(data);
      this.gridAPI()?.refreshCells();
    }

    // if the invoice status is changed to declined or from declined,
    // we need to clear the posting period if it's before the current open month
    if (
      event.column.getColId() === 'invoice_status' &&
      event.oldValue === InvoiceStatus.STATUS_DECLINED
    ) {
      const trial = this.mainQuery.getSelectedTrial();

      if (
        trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
        trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
      ) {
        const { currentOpenMonth } = this.mainQuery.getValue();
        const data = {
          ...event.data,
          accrual_period: event.data.accrual_period
            ? dayjs(`${event.data.accrual_period}-01`).isBefore(currentOpenMonth)
              ? null
              : event.data.accrual_period
            : null,
        };

        event.node.setData(data);
        this.gridAPI()?.refreshCells();
      }
    }

    this.checkChanges();
  }

  private changedFromPaidToUnpaid = (updatedInvoice: IInvoicesGridData) => {
    const initialInvoice = find(this.initialValues, { id: updatedInvoice.id });
    const isUnpaid = (invoice: IInvoicesGridData): boolean => {
      const endOfAccrualPeriod = dayjs(invoice.accrual_period).endOf('month');

      return !invoice.payment_date || dayjs(invoice.payment_date).isAfter(endOfAccrualPeriod);
    };

    return (
      updatedInvoice.has_prepaid &&
      updatedInvoice.accrual_period &&
      initialInvoice?.accrual_period &&
      isUnpaid(updatedInvoice) &&
      !isUnpaid(initialInvoice)
    );
  };

  checkChanges() {
    const currentValues: IInvoicesGridData[] = [];

    const gridAPI = this.gridAPI();

    if (!gridAPI) {
      return;
    }

    gridAPI.forEachNode(({ data }) => {
      currentValues.push(data);
    });

    this.hasChanges.set(!isEqual(this.initialValues, currentValues));
  }

  checkError = (
    params: CellClassParams<IInvoicesGridData>,
    cause: ArrElement<ErrorRow['causes']>
  ) => {
    const row = this.errorRows()[params.data?.id || ''];

    if (!this.showErrors() || !row) {
      return false;
    }

    return row.causes.includes(cause);
  };

  checkIfInvoiceStatusChanged = (invoice: InvoiceModel) => {
    return (
      invoice.invoice_status !==
      this.initialValues.find((initialInvoice) => initialInvoice.id === invoice.id)?.invoice_status
    );
  };

  checkInvalidCostCategorization(value: boolean) {
    this.showInvalidCostCategorization.set(value);
    this.datasource.updateOptions({
      overrideFilter: {
        invalid_cost_categorization: value,
        require_accrual_period: this.showRequireAccrualPeriod(),
        require_cost_breakdown: this.showRequireCostBreakdown(),
        show_unpaid_invoices: this.showUnpaidInvoices(),
      },
    });

    this.datasource.forceRefresh();
  }

  checkRequireAccrualPeriod(value: boolean) {
    this.showRequireAccrualPeriod.set(value);
    this.datasource.updateOptions({
      overrideFilter: {
        invalid_cost_categorization: this.showInvalidCostCategorization(),
        require_accrual_period: value,
        require_cost_breakdown: this.showRequireCostBreakdown(),
        show_unpaid_invoices: this.showUnpaidInvoices(),
      },
    });

    this.datasource.forceRefresh();
  }

  checkRequireCostBreakdown(value: boolean) {
    this.showRequireCostBreakdown.set(value);
    this.datasource.updateOptions({
      overrideFilter: {
        invalid_cost_categorization: this.showInvalidCostCategorization(),
        require_accrual_period: this.showRequireAccrualPeriod(),
        require_cost_breakdown: value,
        show_unpaid_invoices: this.showUnpaidInvoices(),
      },
    });

    this.datasource.forceRefresh();
  }

  checkUnpaidInvoices(value: boolean) {
    this.showUnpaidInvoices.set(value);
    this.datasource.updateOptions({
      overrideFilter: {
        invalid_cost_categorization: this.showInvalidCostCategorization(),
        require_accrual_period: this.showRequireAccrualPeriod(),
        require_cost_breakdown: this.showRequireCostBreakdown(),
        show_unpaid_invoices: value,
      },
    });

    this.datasource.forceRefresh();
  }

  constructor() {
    this.invoicesPageService.expandableIntegrationLineItems$.next([]);
    // vendor currency effect
    effect(() => {
      const gridAPI = this.gridAPI();

      if (!gridAPI) {
        return;
      }

      /*
        if the investigator total column is showing then the breakdown values are visible
        which means we want to keep the trial currency values hidden if the user toggles to trial currency
      */
      if (gridAPI.getColumn('investigator_contract_amount')?.isVisible()) {
        AgSetColumnsVisible({
          gridApi: gridAPI,
          keys: [
            'vendor_currency',
            'fx_rate',
            'total_amount',
            'investigator_amount',
            'passthrough_amount',
            'service_amount',
            'discount_amount',
          ],
          visible: !this.isVendorCurrency(),
        });
      } else {
        AgSetColumnsVisible({
          gridApi: gridAPI,
          keys: ['vendor_currency', 'fx_rate', 'total_amount'],
          visible: !this.isVendorCurrency(),
        });
      }

      setTimeout(() => {
        this.invoiceService.generatePinnedRow();
      }, 0);
    });

    // is_deposit effect
    effect(() => {
      const gridAPI = this.gridAPI();
      const invoicesDepositsEnabled = this.invoicesDepositsEnabled();

      if (!gridAPI) {
        return;
      }

      gridAPI.setColumnVisible('is_deposit', invoicesDepositsEnabled);
    });

    // has_prepaid effect
    effect(() => {
      const gridAPI = this.gridAPI();
      const prepaidEnabled = this.prepaidEnabled();

      if (!gridAPI) {
        return;
      }

      gridAPI.setColumnVisible('has_prepaid', prepaidEnabled);
    });

    this.invoiceService.initialize().pipe(takeUntilDestroyed()).subscribe();

    this.organizationService
      .listVendorAmountTypes()
      .pipe(
        finalize(() => {
          this.listVendorAmountTypesLoading.set(false);
          this.gridAPI()?.refreshCells({ force: true });
        }),
        takeUntilDestroyed()
      )
      .subscribe((result) => {
        if (result.success) {
          this.vendorCostCategories = result.data || [];
        }
      });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.invoiceService.setAccrualPeriodsAndVendorFilter();
      });

    this.eventService
      .select$(EventType.INVOICE_UPDATED)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.invoiceService.newInvoiceCreated$.getValue()) {
          this.refresh$.next();
          this.invoiceService.newInvoiceCreated$.next(false);
        }
      });

    merge(
      this.eventService.select$(EventType.REFRESH_BILL_COM),
      this.eventService.select$(EventType.REFRESH_COUPA),
      this.eventService.select$(EventType.REFRESH_DYNAMICS365),
      this.eventService.select$(EventType.REFRESH_DYNAMICS365_FO),
      this.eventService.select$(EventType.REFRESH_FLAT_FILE_INTEGRATION),
      this.eventService.select$(EventType.REFRESH_NETSUITE),
      this.eventService.select$(EventType.REFRESH_ORACLE_FUSION),
      this.eventService.select$(EventType.REFRESH_QUICKBOOKS_ONLINE),
      this.eventService.select$(EventType.REFRESH_SAGE_INTACCT),
      this.eventService.select$(EventType.BULK_INVOICE_TEMPLATE_UPLOADED),
      this.refresh$
    )
      .pipe(
        startWith(null),
        switchMap(() => this.savingChanges$),
        filter((isSavingChanges) => !isSavingChanges)
      )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.selectedBudgetCurrencyType.set(BudgetCurrencyType.VENDOR);
        this.files.clear();
        this.datasource.forceRefresh();
      });

    getQueryParamData();
    this.subscribeToAnalytics();
  }

  doesInvoiceMappingsMatchTotal = (params: ICellRendererParams): boolean => {
    const initialInvoice = find(this.initialValues, { id: params.data.id });

    if (!params.data.organization.id || !initialInvoice) {
      return false;
    }

    return (
      params.data.organization.id === initialInvoice.organization.id &&
      decimalEquality(
        Number(params.data.expense_amounts.invoice_total.value),
        initialInvoice.expense_amounts.invoice_total.value,
        2
      ) &&
      params.data.does_invoice_mappings_match_total
    );
  };

  enableEditMode = () => {
    this.editMode.set(true);
    this.onEditModeOn();
  };

  async fetchDocumentsForPage() {
    const entity_ids: string[] = [];
    const gridAPI = this.gridAPI();

    if (!gridAPI) {
      return;
    }

    const rowNodes: { file: unknown; node: IRowNode }[] = [];

    gridAPI.getRenderedNodes()?.forEach((node) => {
      if (node.data?.id) {
        if (this.files.has(node.data.id)) {
          const file = this.files.get(node.data.id);

          if (file) {
            rowNodes.push({ file, node });
          }
        } else {
          entity_ids.push(node.data.id);
        }
      }
    });

    // update the rows in the next tick
    setTimeout(() => {
      rowNodes.forEach((row) => {
        row.node.setData({ ...row.node.data, file: row.file });
      });
    }, 0);

    const trialKey = this.mainQuery.getValue().trialKey;

    if (!entity_ids.length) {
      return;
    }

    try {
      const [invoice_docs, supporting_docs] = await Promise.all([
        this.apiService.getFilesByFilters(
          `trials/${trialKey}/vendors/`,
          undefined,
          EntityType.INVOICE,
          DocumentType.DOCUMENT_INVOICE,
          entity_ids
        ),
        this.apiService.getFilesByFilters(
          `trials/${trialKey}/vendors/`,
          undefined,
          EntityType.INVOICE,
          DocumentType.DOCUMENT_INVOICE_SUPPORT,
          entity_ids
        ),
      ]);

      const combinedDocuments = [...invoice_docs, ...supporting_docs];

      entity_ids.forEach((id) => {
        const rowNode = gridAPI.getRowNode(id);

        if (rowNode) {
          const file = combinedDocuments.find((f) => f.entity_id?.includes(id));

          rowNode.setData({ ...rowNode.data, file });
          this.files.set(id, file || null);
          gridAPI.redrawRows({ rowNodes: [rowNode] });
        }
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }

  firstDataRendered({ api }: { api: GridApi }) {
    api.sizeColumnsToFit();
    api.setColumnVisible('is_deposit', this.invoicesDepositsEnabled());
  }

  getCauses = (currentData: IInvoicesGridData, originalData: IInvoicesGridData) => {
    const causes: ArrElement<ErrorRow['causes']>[] = [];

    const status = this.mainQuery.getSelectedTrial()?.implementation_status;
    const shouldLookAtTheDates =
      status &&
      [
        TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED,
        TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE,
      ].includes(status);

    if (shouldLookAtTheDates) {
      if (currentData.payment_date && originalData.payment_date !== currentData.payment_date) {
        if (dayjs(currentData.payment_date).isBefore(this.mainQuery.getValue().currentOpenMonth)) {
          causes.push('payment_date');
        }
      }

      if (
        currentData.accrual_period &&
        originalData.accrual_period !== currentData.accrual_period
      ) {
        if (
          dayjs(`${currentData.accrual_period}-01`).isBefore(
            this.mainQuery.getValue().currentOpenMonth
          )
        ) {
          causes.push('accrual_period');
        }
      }
    }

    if (
      currentData.services_period &&
      originalData.services_period !== currentData.services_period
    ) {
      if (
        dayjs(currentData.services_period).isBefore(
          this.mainQuery.getValue().trialStartDate,
          'month'
        ) ||
        dayjs(currentData.services_period).isAfter(this.mainQuery.getValue().trialEndDate, 'month')
      ) {
        causes.push('services_period_trial_timeline');
      }
    }

    if (!this.isJEConfigSetToAttribute()) {
      if (!isInvoiceTotalEqual(currentData)) {
        causes.push('totals');
      }
    }

    return causes;
  };

  getEditableClass =
    (classes: string[] = []) =>
    (params: CellClassParams) => {
      const cls = [...classes];

      if (this.editMode()) {
        if (
          params.column.isCellEditable(params.node) ||
          (params.column.getColId() === 'is_deposit' ? this.isRowEditable(params) : false) ||
          (params.column.getColId() === 'has_prepaid' ? !params.data.hasPrepaidInitialValue : false)
        ) {
          cls.push(TableConstants.STYLE_CLASSES.EDITABLE_CELL);
        } else {
          cls.push(TableConstants.STYLE_CLASSES.NOT_EDITABLE_CELL, 'cursor-not-allowed');
        }
      }

      return cls;
    };

  private getExpenseAmounts(invoice: InvoiceDataStream) {
    return {
      discount_total: this.mapExpenseAmount(
        AmountType.AMOUNT_DISCOUNT,
        invoice.discount_contract_amount,
        invoice,
        true
      ),
      discount_total_trial_currency: this.mapExpenseAmount(
        AmountType.AMOUNT_DISCOUNT,
        invoice.discount_amount,
        invoice,
        false
      ),
      investigator_total: this.mapExpenseAmount(
        AmountType.AMOUNT_INVESTIGATOR,
        invoice.investigator_contract_amount,
        invoice,
        true
      ),
      investigator_total_trial_currency: this.mapExpenseAmount(
        AmountType.AMOUNT_INVESTIGATOR,
        invoice.investigator_amount,
        invoice,
        false
      ),
      invoice_total: this.mapExpenseAmount(
        AmountType.AMOUNT_TOTAL,
        invoice.total_contract_amount,
        invoice,
        true
      ),
      invoice_total_trial_currency: this.mapExpenseAmount(
        AmountType.AMOUNT_TOTAL,
        invoice.total_amount,
        invoice,
        false
      ),
      pass_thru_total: this.mapExpenseAmount(
        AmountType.AMOUNT_PASSTHROUGH,
        invoice.passthrough_contract_amount,
        invoice,
        true
      ),
      pass_thru_total_trial_currency: this.mapExpenseAmount(
        AmountType.AMOUNT_PASSTHROUGH,
        invoice.passthrough_amount,
        invoice,
        false
      ),
      services_total: this.mapExpenseAmount(
        AmountType.AMOUNT_SERVICE,
        invoice.service_contract_amount,
        invoice,
        true
      ),
      services_total_trial_currency: this.mapExpenseAmount(
        AmountType.AMOUNT_SERVICE,
        invoice.service_amount,
        invoice,
        false
      ),
    };
  }

  getGridOptions() {
    return computed(() => {
      const isJEConfigSetToAttribute = this.isJEConfigSetToAttribute();

      this.showInvoicesPaymentStatus();
      this.showInvoicesPaymentDate();
      this.userHasApproveInvoicePermission();
      this.showInvoiceDetailLink();
      this.invoicesDepositsEnabled();
      this.prepaidEnabled();

      return untracked(() => {
        return <GridOptions>{
          cacheBlockSize: 250,
          defaultColDef: {
            ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
            editable: (params) => {
              if (
                (params.column.getColId() === 'vendor_id' && params.data.has_prepaid) ||
                (params.column.getColId() === 'total_contract_amount' && isJEConfigSetToAttribute)
              ) {
                return false;
              }

              return (
                this.editMode() &&
                !params.node.rowPinned &&
                this.isRowEditable(params) &&
                !this.savingChanges()
              );
            },
            headerClass: ['text-center'],
            // onCellValueChanged: (params) => {
            onCellEditingStarted: () => {
              this.checkChanges();
            },
            resizable: true,
          },
          getRowHeight: (params) => {
            if (params.node.detail) {
              return params.data.invoice_line_item_count * 40;
            } else {
              return undefined;
            }
          },
          getRowId: ({ data }) => data.id,
          isRowSelectable: (rowNode: IRowNode<IInvoicesGridData>) => {
            const status = this.mainQuery.getSelectedTrial()?.implementation_status;

            const data = rowNode.data;
            const originalData = this.initialValues.find((v) => v.id === data?.id);

            if (data?.hasPrepaidInitialValue || data?.has_prepaid) {
              rowNode.setSelected(false);

              return false;
            }

            if (!status || !originalData) {
              return true;
            }

            if (
              status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED &&
              status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
            ) {
              return true;
            }

            return !(
              originalData.accrual_period &&
              dayjs(`${originalData.accrual_period}-01`).isBefore(
                this.mainQuery.getValue().currentOpenMonth
              )
            );
          },
          onRowGroupOpened: (event: RowGroupOpenedEvent) => {
            if (
              event.node.expanded &&
              !this.invoicesPageService.expandableIntegrationLineItems$
                .getValue()
                .some((invoice) => invoice.invoice_id === event.data.id)
            ) {
              this.gqlService
                .listInvoiceCostCategoryLineItems$(event.data.id)
                .subscribe((response) => {
                  if (response.success) {
                    const attributes: IntegrationLineItem[] =
                      response.data?.map((lineItem) => ({
                        amount: lineItem.amount || 0,
                        attr1_value:
                          lineItem.attributes.find(
                            (attribute) =>
                              attribute.attribute_name ===
                              this.globalStore.primaryAttribute.attribute_name()
                          )?.attribute_value || '',
                        attr2_value:
                          lineItem.attributes.find(
                            (attribute) =>
                              attribute.attribute_name ===
                              this.globalStore.secondaryAttribute.attribute_name()
                          )?.attribute_value || '',
                        isPivotedOn2Attr: !!this.globalStore.secondaryAttribute.attribute_name(),
                      })) || [];

                    this.invoicesPageService.expandableIntegrationLineItems$.next([
                      ...this.invoicesPageService.expandableIntegrationLineItems$.getValue(),
                      {
                        attributes: [...attributes],
                        invoice_id: event.data.id,
                      },
                    ]);
                  }
                });
            }
          },
          pagination: true,
          paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_INVOICES),
          processCellForClipboard: (params: ProcessCellForExportParams) => {
            if (params.column.getColId() === 'invoice_status') {
              return this.invoicesGridFormatterService.getFormattedInvoiceStatus(params.value);
            }

            if (params.column.getColId() === 'payment_status') {
              return this.invoicesGridFormatterService.getFormattedPaymentStatus(params.value);
            }

            return params.formatValue(params.value);
          },
          rowHeight: 40,
          suppressPaginationPanel: true,
          ...omit(TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS, [
            'processCellForClipboard',
            'rowHeight',
          ]),
          columnDefs: InvoicesColumnDefs(this).filter(Boolean),
          enableRangeSelection: true,
          fillHandleDirection: 'y',
          fillOperation: (params: FillOperationParams<IInvoicesGridData>) => {
            const val = params.values[0];

            if (
              params.column.getColId() === 'accrual_period' ||
              params.column.getColId() === 'payment_date' ||
              params.column.getColId() === 'invoice_date' ||
              params.column.getColId() === 'due_date'
            ) {
              return val;
            }

            if (params.column.getColId() === 'purchase_order_id') {
              // if the po is empty it should empty other rows.
              if (val == null) {
                return null;
              }

              const isPOinOrganization = this.isPOinOrganization(
                val,
                params.rowNode.data?.organization?.id || ''
              );

              return isPOinOrganization ? val : params.rowNode.data?.po_reference;
            }

            return false;
          },
          getContextMenuItems: (params: GetContextMenuItemsParams) => {
            return [
              'cut',
              'copy',
              'copyWithHeaders',
              'copyWithGroupHeaders',
              {
                action: () => {
                  params.api.pasteFromClipboard();
                },
                cssClasses: ['custom-paste-item'],
                disabled: !this.editMode(),
                icon: '<span class="ag-icon ag-icon-paste"></span>',
                name: 'Paste',
                shortcut: 'Ctrl+V',
              },
              'separator',
              'export',
            ];
          },
          onModelUpdated: (event: ModelUpdatedEvent) => {
            if (event.api.getDisplayedRowCount() === 0) {
              event.api.showNoRowsOverlay();
            } else {
              event.api.hideOverlay();
            }
          },
          processCellFromClipboard: (params: ProcessCellForExportParams<IInvoicesGridData>) => {
            if (params.column.getColId() === 'purchase_order_id') {
              // if the po is empty it should empty other rows.
              if (params.value == null || params.value === '') {
                return null;
              }

              const isPOinOrganization = this.isPOinOrganization(
                params.value,
                params.node?.data?.organization?.id || ''
              );

              return isPOinOrganization ? params.value : params.node?.data?.po_reference;
            }

            return params.value;
          },
          rowSelection: 'multiple',
          suppressCellFocus: false,
          suppressMenuHide: true,
          suppressRowClickSelection: true,
        };
      });
    });
  }

  getInvoiceStatusChangeReason = (invoice: InvoiceModel, status: InvoiceStatus) =>
    this.checkIfInvoiceStatusChanged(invoice) && invoice.invoice_status === status
      ? 'In-line Status Change'
      : null;

  private getSuccessSaveModalMessage(
    hasNewPrepaids: boolean,
    someChangedFromPaidToUnpaid: boolean
  ): string {
    const successText = '<b>Your invoices have been saved successfully</b>';

    if (hasNewPrepaids && someChangedFromPaidToUnpaid) {
      return `${successText}. One or more invoices have been marked with prepaid impact, and one or more invoices linked to prepaids have been marked as unpaid. Would you like to adjust your prepaid values now?`;
    }

    if (hasNewPrepaids) {
      return `${successText}, but one or more have been marked with prepaid impact. Would you like to edit those prepaid values now?`;
    }

    return `${successText}, but one or more invoices linked to prepaids have been marked as unpaid. Would you like to adjust your prepaid values now?`;
  }

  hasInvoiceCategoryOutsideBudget = (params: ICellRendererParams): boolean => {
    if (!params.data.organization.id || !params.data.expense_amounts.invoice_total.value) {
      return false;
    }

    const vendor = this.vendorCostCategories.find(
      (vendor) => vendor.organization_id === params.data.organization.id
    );

    return (
      (!!params.data.expense_amounts.services_total.value &&
        !vendor?.amount_types.includes(AmountType.AMOUNT_SERVICE)) ||
      (!!params.data.expense_amounts.discount_total.value &&
        !vendor?.amount_types.includes(AmountType.AMOUNT_DISCOUNT)) ||
      (!!params.data.expense_amounts.investigator_total.value &&
        !vendor?.amount_types.includes(AmountType.AMOUNT_INVESTIGATOR)) ||
      (!!params.data.expense_amounts.pass_thru_total.value &&
        !vendor?.amount_types.includes(AmountType.AMOUNT_PASSTHROUGH))
    );
  };

  isCategoryOutsideBudget = (
    params: CellClassParams<IInvoicesGridData>,
    field: string
  ): boolean => {
    if (params.data?.organization.id && !!params.data?.expense_amounts.invoice_total.value) {
      const vendor = this.vendorCostCategories.find(
        (vendor) => vendor.organization_id === params.data?.organization.id
      );

      if (field === 'services_total') {
        return (
          !!params.data.expense_amounts.services_total.value &&
          !vendor?.amount_types.includes(AmountType.AMOUNT_SERVICE)
        );
      }

      if (field === 'discount_total') {
        return (
          !!params.data.expense_amounts.discount_total.value &&
          !vendor?.amount_types.includes(AmountType.AMOUNT_DISCOUNT)
        );
      }

      if (field === 'investigator_total') {
        return (
          !!params.data.expense_amounts.investigator_total.value &&
          !vendor?.amount_types.includes(AmountType.AMOUNT_INVESTIGATOR)
        );
      }

      if (field === 'pass_thru_total') {
        return (
          !!params.data.expense_amounts.pass_thru_total.value &&
          !vendor?.amount_types.includes(AmountType.AMOUNT_PASSTHROUGH)
        );
      }
    }

    return false;
  };

  isPOinOrganization = (po_id: string, org_id: string) => {
    return this.invoiceService.purchaseOrdersQuery.getAll().some((po) => {
      return po.id === po_id && po.organization?.id === org_id;
    });
  };

  isRowEditable = (params: EditableCallbackParams<IInvoicesGridData>) => {
    if (['create_date', 'created_by'].includes(params.column.getColId())) {
      return false;
    }

    const status = this.mainQuery.getSelectedTrial()?.implementation_status;

    const data = params.data;
    const originalData = this.initialValues.find((v) => v.id === data?.id);
    const openMonth = this.mainQuery.getValue().currentOpenMonth;

    if (!data || !status || !originalData) {
      return true;
    }

    if (
      status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED &&
      status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
    ) {
      return true;
    }

    if (
      params.column.getColId() === 'payment_status' ||
      params.column.getColId() === 'services_period'
    ) {
      return true;
    }

    if (params.column.getColId() === 'payment_date') {
      const date = originalData.payment_date;

      return !(date && dayjs(date).isBefore(openMonth));
    }

    if (data.invoice_status === 'STATUS_DECLINED') {
      return params.column.getColId() !== 'accrual_period';
    }

    if (originalData.accrual_period) {
      if (originalData.invoice_status === InvoiceStatus.STATUS_DECLINED) {
        return true;
      }

      if (dayjs(`${originalData.accrual_period}-01`).isBefore(openMonth)) {
        return false;
      }
    }

    return true;
  };

  isRowMaster = (dataItem: { invoice_line_item_count: number }) => {
    return dataItem.invoice_line_item_count > 0;
  };

  private mapExpenseAmount(
    amountType: AmountType,
    value: null | number | undefined,
    invoice: InvoiceDataStream,
    is_vendor_currency_amount: boolean
  ) {
    if (is_vendor_currency_amount) {
      return {
        contract_curr: invoice.vendor_currency || Currency.USD,
        is_vendor_currency_amount,
        type: amountType,
        value: value || 0,
      };
    } else {
      return {
        exchange_rate: invoice.fx_rate || 1,
        is_vendor_currency_amount,
        type: amountType,
        value: value || 0,
      };
    }
  }

  async modelUpdated(event: ModelUpdatedEvent): Promise<void> {
    if (event.keepRenderedRows) {
      await this.fetchDocumentsForPage();
      setTimeout(() => {
        this.invoiceService.generatePinnedRow();
      });
    }
  }

  onBulkApplyButtonClick = async () => {
    const gridApi = this.gridAPI();

    if (!gridApi) {
      return;
    }

    const ref = this.overlayService.openPopup<
      undefined,
      BulkEditInvoicesResponse,
      BulkEditInvoicesComponent
    >({
      content: BulkEditInvoicesComponent,
      settings: {
        header: `Bulk Edit: ${this.selectedRows().length} Selected Invoices`,
        primaryButton: {
          action: (instance) => instance?.onSubmit(),
          disabled: (instance) => !!instance?.isApplyButtonDisabled(),
          label: 'Apply',
          tooltip: (instance) =>
            instance?.isApplyButtonDisabled() ? 'At least 1 field required to Apply Changes.' : '',
        },
      },
    });

    const { data } = await firstValueFrom(ref.afterClosed$);

    if (data?.success) {
      gridApi.applyServerSideTransaction({
        update: this.selectedRows().map((invoice) => {
          const { accrualPeriod, paymentStatus, purchaseOrder, servicesPeriod, status, vendor } =
            data.data;
          const getVal = (val: null | string) => (val === '' ? null : val);

          const isNewVendor = vendor == null ? false : vendor !== invoice.organization.id;
          const po_reference =
            purchaseOrder == null
              ? isNewVendor
                ? null
                : invoice.po_reference
              : getVal(purchaseOrder);

          return {
            ...invoice,
            accrual_period: accrualPeriod == null ? invoice.accrual_period : getVal(accrualPeriod),
            invoice_status: status == null ? invoice.invoice_status : status,
            organization: isNewVendor
              ? {
                  ...invoice.organization,
                  id: vendor,
                  name: this.invoiceService.vendorOptions.find(({ value }) => value === vendor)
                    ?.label,
                }
              : invoice.organization,
            payment_status: paymentStatus == null ? invoice.payment_status : getVal(paymentStatus),
            po_reference,
            services_period:
              servicesPeriod == null ? invoice.services_period : getVal(servicesPeriod),
          };
        }),
      });
      this.gridAPI()?.refreshCells({
        columns: ['invoice_no'],
        force: true,
      });
      this.checkChanges();
    }
  };

  onCancel = () => {
    this.resetEditMode();
    this.datasource.forceRefresh();
  };

  onCellValueChanged = (params: NewValueParams): void => {
    const oldData = this.initialValues.find((v) => v.id === params.data.id);

    if (!oldData) {
      return;
    }

    const causes = this.getCauses(params.data, oldData);

    this.errorRows.update((errors) => {
      return {
        ...errors,
        [params.data.id]: { causes, invoice: params.data },
      };
    });

    if (params.node) {
      params.api.refreshCells({
        force: true,
        rowNodes: [params.node],
      });
    }
  };

  onEditModeOff(): void {
    this.invoiceService.filtersForm.enable();
    this.gridAPI()?.setGridOption('defaultColDef', {
      ...this.gridOptions().defaultColDef,
      sortable: true,
    });
    this.gridAPI()?.setColumnVisible('checkbox', false);
    this.gridAPI()?.refreshCells({ force: true });
  }

  onEditModeOn(): void {
    this.invoiceService.filtersForm.disable();
    this.gridAPI()?.startEditingCell({
      colKey: 'invoice_no',
      rowIndex: 0,
    });
    this.gridAPI()?.setGridOption('defaultColDef', {
      ...this.gridOptions().defaultColDef,
      sortable: false,
    });
    this.gridAPI()?.setColumnVisible('checkbox', true);
  }

  async onExportInvoices() {
    const gridAPI = this.gridAPI();

    if (!gridAPI) {
      return;
    }

    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs().format('YYYY.MM.DD-HHmmss');

    const { errors, success } = await this.processExportEvent(trialName, dateStr);

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  onGridReady({ api }: GridReadyEvent) {
    api.sizeColumnsToFit();
    this.gridAPI.set(api);
    this.invoiceService.setGridApi(api);

    this.datasource.initialize({
      filters: this.serverSideFilters,
      filterValues$: this.filterValues$,
      parseFunction: (items) => {
        const gridData = items?.map((invoice, index) => {
          return {
            ...invoice,
            accrual_period: invoice.accrual_period
              ? dayjs(invoice.accrual_period).format('YYYY-MM')
              : null,
            attributes:
              index % 2
                ? [
                    { attr_name: '1', attr_total: 200 },
                    { attr_name: '2', attr_total: 100 },
                  ]
                : [],
            cards: [],
            expense_amounts: this.getExpenseAmounts(invoice),
            file: undefined,
            hasError: false,
            hasPrepaidInitialValue: invoice.has_prepaid,
            initialRowIndex: index,
            invoice_line_item_count: invoice.invoice_line_item_count || 0,
            line_items: JSON.parse(invoice.line_items || '[]'),
            ocr_line_items: JSON.parse(invoice.ocr_line_items || '[]'),
            organization: {
              currency: invoice.vendor_currency,
              id: invoice.vendor_id,
              name: invoice.vendor_name,
            },
            po_reference: invoice.purchase_order_id,
            services_period: invoice.services_period
              ? dayjs(invoice.services_period).format('YYYY-MM')
              : null,
          } as unknown as IInvoicesGridData;
        });

        this.initialValues = cloneDeep(gridData);

        return gridData;
      },
      sortModel$: this.sortModel$,
      untilDestroyedPipeOperator: takeUntilDestroyed(this.destroyRef),
    });

    this.datasource.updateOptions({
      overrideFilter: {
        invalid_cost_categorization: this.showInvalidCostCategorization(),
        require_accrual_period: this.showRequireAccrualPeriod(),
        require_cost_breakdown: this.showRequireCostBreakdown(),
        show_unpaid_invoices: this.showUnpaidInvoices(),
      },
    });

    this.vendorHasOneCurrencySubscription?.unsubscribe();
    this.vendorHasOneCurrencySubscription = this.datasource.aggregation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.vendorHasOneCurrency = !(
          Object.prototype.hasOwnProperty.call(value, 'vendor_currency') &&
          !value.vendor_currency &&
          this.datasource.totalRows !== 0
        );
        this.isVendorCurrenciesUSD =
          Object.prototype.hasOwnProperty.call(value, 'vendor_currency') &&
          value.vendor_currency === Currency.USD;
      });
  }

  onNewInvoice(vendorId?: string) {
    if (this.trialAttributesLoading()) {
      setTimeout(() => {
        this.onNewInvoice(vendorId);
      }, 0);

      return;
    }

    this.overlayService.openPopup({
      content: NewInvoiceDialogComponent,
      data: { hideFirstTab: this.isJEConfigSetToAttribute(), vendorId },
      settings: {
        header: 'Invoice Upload',
        primaryButton: {
          action: (instance: NewInvoiceDialogComponent | null) => instance?.uploadInvoice(),
        },
      },
    });
  }

  onSave = async () => {
    const gridAPI = this.gridAPI();

    if (!gridAPI) {
      return;
    }

    this.paginationPageSize.set(gridAPI.paginationGetPageSize());

    const currentValues: IInvoicesGridData[] = [];

    gridAPI.forEachNode(({ data }) => {
      currentValues.push({
        ...data,
      });
    });

    const changedRows = differenceWith(currentValues, this.initialValues, (obj1, obj2) => {
      return isEqual(omit(obj1, ['file']), omit(obj2, ['file']));
    });

    if (changedRows.length) {
      const errorRows: {
        causes: ('accrual_period' | 'payment_date' | 'services_period_trial_timeline' | 'totals')[];
        invoice: IInvoicesGridData;
      }[] = [];

      changedRows.forEach((invoice) => {
        const originalData = this.initialValues.find((v) => v.id === invoice.id);
        const currentData = currentValues.find((v) => v.id === invoice.id);

        if (!originalData || !currentData) {
          return;
        }

        const causes = this.getCauses(currentData, originalData);

        if (causes.length) {
          errorRows.push({ causes, invoice: originalData });
        }
      });

      this.errorRows.set(
        errorRows.reduce(
          (acc, obj) => {
            acc[obj.invoice.id] = obj;

            return acc;
          },
          {} as Record<string, ErrorRow>
        )
      );

      if (errorRows.length) {
        this.overlayService.error(
          errorRows.reduce((acc, { causes, invoice }) => {
            const invoiceNo = `Invoice #${invoice.invoice_no || ''} -`;

            causes.forEach((cause) => {
              if (cause === 'payment_date') {
                acc.push(`${invoiceNo} Payment Date cannot be set in a Closed Month.`);
              }

              if (cause === 'accrual_period') {
                acc.push(`${invoiceNo} Posting Period cannot be set to a Closed Month.`);
              }

              if (cause === 'services_period_trial_timeline') {
                acc.push(
                  `${invoiceNo} Service Period must be set to a date in the trial timeline.`
                );
              }

              if (cause === 'totals') {
                acc.push(`${invoiceNo} ${MessagesConstants.INVOICE.TOTAL_VALIDATION}`);
              }
            });

            return acc;
          }, [] as string[]),
          undefined,
          true
        );
        this.showErrors.set(true);
        gridAPI.refreshCells();
        this.savingChanges.set(false);

        return;
      }
    }

    this.savingChanges.set(true);
    //gives the user the option to leave the page while it is being saved without confirm modal
    this.hasChanges.set(false);

    if (changedRows.length > 0) {
      this.waitingUpdateQuarterAccrualsEvent.set(true);
    }

    await this.invoicesPageService.batchSaveInvoices({
      changedRows,
      checkIfInvoiceStatusChanged: this.checkIfInvoiceStatusChanged,
      getInvoiceStatusChangeReason: this.getInvoiceStatusChangeReason,
      initialValues: this.initialValues,
      isNewPrepaidImpactRows: !!this.newPrepaidImpactRows.length,
      periodCloseComponent: this.periodCloseComponent,
      rowsToDelete: this.rowsToDelete(),
      shouldShowMarkedAsUnpaidModal: this.shouldShowMarkedAsUnpaidModal(changedRows),
      successSaveModalMessage: this.getSuccessSaveModalMessage(
        !!this.newPrepaidImpactRows.length,
        this.shouldShowMarkedAsUnpaidModal(changedRows)
      ),
      vendorIdToNavigate:
        this.newPrepaidImpactRows.sort((a, b) => a.index - b.index)[0]?.vendorId || '',
    });

    this.resetEditMode();
    this.savingChanges.set(false);
  };

  async paginationChange(): Promise<void> {
    await this.fetchDocumentsForPage();
    setTimeout(() => {
      this.invoiceService.generatePinnedRow();
    });
  }

  processExportEvent = (trialName: string, dateStr: string) => {
    return firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.INVOICES,
          filename: `${trialName}_auxilius-invoices__${dateStr}`,
          filter_model: this.datasource.currentServerInput?.filter_model || [],
          invalid_cost_categorization: this.showInvalidCostCategorization(),
          require_accrual_period: this.showRequireAccrualPeriod(),
          require_cost_breakdown: this.showRequireCostBreakdown(),
          show_unpaid_invoices: this.showUnpaidInvoices(),
          sort_model: this.sortModel$.getValue(),
          trial_currency_selected: !this.isVendorCurrency(),
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );
  };

  removeInvoices = async () => {
    const invoiceNumbers = this.selectedRows()
      .map(({ invoice_no }) => invoice_no)
      .filter((number) => number);
    const amountOfInvoicesWithoutNumber = this.selectedRows().length - invoiceNumbers.length;
    const invoiceNumbersMessage = amountOfInvoicesWithoutNumber
      ? `${invoiceNumbers.join(', ')} and ${amountOfInvoicesWithoutNumber} more`
      : invoiceNumbers.join(', ');
    const message =
      amountOfInvoicesWithoutNumber === this.selectedRows().length
        ? 'Are you sure you want to remove selected Invoices?'
        : `Are you sure you want to remove following Invoices: ${invoiceNumbersMessage}?`;

    const resp = this.overlayService.openPopup<
      ReasonModalData,
      ReasonModalResponse,
      ReasonModalModalComponent
    >({
      content: ReasonModalModalComponent,
      data: {
        message,
      },
      settings: {
        header: 'Remove Invoices',
        primaryButton: {
          action: (instance: null | ReasonModalModalComponent) => instance?.save(),
          label: 'Remove',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      const rows = this.selectedRows();

      this.rowsToDelete.set([
        ...rows.map((invoice) => ({ invoice, reason: event.data?.reason || '' })),
      ]);
      this.gridAPI()?.applyServerSideTransaction({ remove: rows });
      this.selectedRows.set([]);
    }

    this.checkChanges();
  };

  resetEditMode() {
    this.showErrors.set(false);
    this.editMode.set(false);
    this.onEditModeOff();
    this.errorRows.set({});
    this.gridAPI()?.deselectAll();
    this.rowsToDelete.set([]);
    this.hasChanges.set(false);
    this.selectedRows.set([]);
    this.isAllRowsOnCurrentPageSelected = false;
    this.newPrepaidImpactRows = [];
  }

  rowSelected(event: RowSelectedEvent) {
    this.selectedRows.set(event.api.getSelectedRows());
  }

  selectionChanged(event: SelectionChangedEvent): void {
    if (event.source === 'uiSelectAll') {
      this.isAllRowsOnCurrentPageSelected = !this.isAllRowsOnCurrentPageSelected;

      // eslint-disable-next-line
      // @ts-ignore
      if (this.isAllRowsOnCurrentPageSelected) {
        this.gridAPI()?.deselectAll();

        if (
          this.gridAPI()?.paginationGetTotalPages() === 1 &&
          this.gridAPI()
            ?.getRenderedNodes()
            .every((node) => node.selectable)
        ) {
          this.gridAPI()?.selectAll();
        }

        this.gridAPI()?.setNodesSelected({
          newValue: true,
          nodes: this.gridAPI()?.getRenderedNodes() || [],
        });
      } else {
        this.gridAPI()?.deselectAll();
      }
    }
  }

  private shouldShowMarkedAsUnpaidModal(changedRows: IInvoicesGridData[]): boolean {
    const featureFlagIsOn = this.launchDarklyService.$select(
      (flags) => flags.journal_entry_ap_manual_clearing
    );

    return featureFlagIsOn() && changedRows.some(this.changedFromPaidToUnpaid);
  }

  subscribeToAnalytics() {
    this.datasource.refresh$
      .pipe(takeUntilDestroyed())
      .pipe(
        switchMap(() =>
          this.datasourceService.retrieveInvoiceAnalytics({
            filter_model: this.datasource.currentServerInput?.filter_model || {},
          })
        )
      )
      .subscribe(({ total_rows }) => {
        this.totalRows.set(total_rows);
        this.showExportFull.set(DataStreamUtils.showExportFullDataset(total_rows));
      });
  }

  tooltipValueGetter = (field: string) => (params: ITooltipParams<IInvoicesGridData>) => {
    const tooltip = get(params, field) || '';

    if (params.node) {
      if (this.editMode() && field === 'data.organization.name' && params.data?.has_prepaid) {
        return MessagesConstants.PREPAIDS.DISABLED_VENDOR;
      }

      const column = params.column as Column;

      if (
        column?.isCellEditable(params.node) ||
        this.isRowEditable(params as EditableCallbackParams<IInvoicesGridData>)
      ) {
        return tooltip;
      } else {
        if (
          column.getColId() === 'accrual_period' &&
          params.data?.invoice_status === InvoiceStatus.STATUS_DECLINED
        ) {
          const status = this.mainQuery.getSelectedTrial()?.implementation_status;

          // eslint-disable-next-line max-depth
          if (
            status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED &&
            status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
          ) {
            return invoiceDisabledTooltip;
          }

          return 'Cannot edit Posting Period for declined invoices.';
        }

        return invoiceDisabledTooltip;
      }
    }

    return tooltip;
  };

  tooltipValueGetterForPrepaidColumn = (params: ICellRendererParams) => {
    if (this.editMode() && !this.userHasUpdatePrepaidPermission() && !params.value) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    } else if (
      this.editMode() &&
      (this.userHasUpdatePrepaidPermission() ||
        (!this.userHasUpdatePrepaidPermission() && params.value))
    ) {
      if (params.data.hasPrepaidInitialValue) {
        return 'To unlink from Prepaids, unlink from the Prepaids page.';
      } else {
        if (!params.data.organization.id) {
          return 'Vendor Contract must be populated to check Prepaid Impact.';
        } else {
          return '';
        }
      }
    } else {
      if (this.isRowEditable(params as EditableCallbackParams)) {
        return InvoiceCheckboxTooltip;
      }

      return '';
    }
  };
}
